/**
 * SidebarNavRow + renderNavRow — shared nav-row primitive for the
 * portal `Sidebar` and the admin `AdminSidebar`.
 *
 * Pre-(m.6) Phase 2.5 each sidebar inlined the same nav-row JSX
 * twice (top-of-page main nav + bottom-of-page settings/log-out
 * group). When the sub-page grouping (Spotlight collapsible group,
 * Documents lower group) landed, the duplicated JSX was no longer
 * tractable. Extracted here so both sidebars share a single source
 * of truth for the row treatment.
 *
 * Spec: meridian-redesign — PR (m.6) Phase 2.5
 * Requirements: 1.5, 14.1, 14.4, 18.5
 */

import type { JSX } from 'react';
import { Link, type Location } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface SidebarNavRowItem {
  name: string;
  /**
   * Optional display label. `name` is used as a stable key across the sidebar
   * (active/pending/subpage logic, role lists), so when the visible text should
   * differ from that key, set `label` — it is rendered in place of `name`.
   */
  label?: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * Decide whether a sidebar nav row should render in its active
 * (highlighted) state for the given current location.
 *
 * Pre-(contestant-admin-ui-polish 14.1) the row used a strict
 * `location.pathname === item.path` comparison. That check fails for
 * sub-page rows whose `item.path` includes a tab search-param —
 * `/admin/approvals?tab=applications`, `/portal/vault?tab=submit`,
 * etc. — because `location.pathname` strips the search portion. The
 * net effect was that the muted-grey "you are here within this
 * product" affordance never persisted on the active sub-page.
 *
 * The fix is path-aware: we split the configured `itemPath` into
 * pathname + search parts and require both to match. Items without
 * search params keep the original pathname-only comparison so the
 * top-level rows (Dashboard, Calendar, ...) behave unchanged.
 *
 * Validates: contestant-admin-ui-polish Requirements 5.1, 5.2, 5.3.
 */
export function isPathActive(
  itemPath: string,
  location: { pathname: string; search: string },
): boolean {
  const [itemPathname, itemSearch = ''] = itemPath.split('?');
  if (location.pathname !== itemPathname) return false;
  if (!itemSearch) return true;
  // For tab-style routes, every search-param the item declares must
  // match the current URL's params. Extra params on the current URL
  // are tolerated — they don't change which sub-page is active.
  const itemParams = new URLSearchParams(itemSearch);
  const currentParams = new URLSearchParams(location.search);
  for (const [key, value] of itemParams.entries()) {
    if (currentParams.get(key) !== value) return false;
  }
  return true;
}

export interface RenderNavRowOptions {
  location: Location;
  renderCollapsed: boolean;
  /** When the row is "Podium" and there are unread channels. */
  hasUnread?: boolean;
  /** When the row is "Approvals" and there are pending applications. */
  hasPending?: boolean;
  /** When set, renders the row indented (used for Spotlight children). */
  nested?: boolean;
  /**
   * Tone — controls the row's resting color.
   * - `default` (the Material-derived foreground state for primary nav)
   * - `muted`   (used for utility destinations like Documents in the lower group)
   */
  tone?: 'default' | 'muted';
  /**
   * Optional click handler fired when the nav row is tapped. Used by
   * the mobile drawer to close immediately on navigation rather than
   * waiting for the route-change useEffect (which can miss same-path
   * navigations).
   */
  onNavClick?: () => void;
  /**
   * Marks this row as the group's default child — i.e. the first
   * subpage rendered inside its `SidebarNavGroup`. When the user
   * navigates to the parent route without a `?tab=` param (e.g.
   * `/admin/approvals` rather than `/admin/approvals?tab=titles`),
   * the page itself defaults to its first tab. The first child row
   * should then render in its active state to mirror what the user
   * is actually looking at.
   *
   * `isPathActive` alone can't satisfy this case: for a child whose
   * `item.path` is `/admin/approvals?tab=applications`, the helper
   * requires `tab=applications` to appear in the URL. When the URL
   * has no `tab` at all, the strict check returns false and no
   * child highlights, leaving the user without a "you are here"
   * affordance on the default subpage.
   *
   * When this flag is true, `renderNavRow` falls through to active
   * state if (a) `location.pathname` matches `item.path`'s pathname
   * portion, and (b) the current URL has no `tab` query param. The
   * second guard ensures the fallback only fires when the user is
   * sitting on the parent route's default tab — once they pick a
   * specific tab, `isPathActive` takes over and the matching tab
   * row wins.
   *
   * Validates: contestant-admin-ui-polish Requirements 5.1, 5.2, 5.3.
   */
  isDefaultForGroup?: boolean;
}

/**
 * Render a single sidebar nav row. Returns the React element.
 * Wrapped in a function (not a component) so React doesn't treat
 * it as a render boundary — keeps key handling, hover-state, and
 * collapsed-tooltip behavior consistent with the inlined version.
 */
export function renderNavRow(
  item: SidebarNavRowItem,
  options: RenderNavRowOptions,
): JSX.Element {
  const { location, renderCollapsed, hasUnread, hasPending, nested, tone = 'default', onNavClick, isDefaultForGroup } =
    options;
  const Icon = item.icon;
  // Normal active match: pathname (and any declared search params)
  // line up with the current URL exactly. See `isPathActive`.
  const exactActive = isPathActive(item.path, location);
  // Default-subpage fallback: when this row is the first child of a
  // `SidebarNavGroup` and the user is sitting on the parent route
  // with no specific `?tab=` selected, the subpage rendered on
  // screen IS this row — light it up so the sidebar reflects what
  // the user is looking at. The current URL must have no `tab`
  // query param; once a tab is chosen, `isPathActive` decides which
  // child highlights and this fallback no longer fires.
  // Validates: contestant-admin-ui-polish Requirements 5.1, 5.2, 5.3.
  const [itemPathname] = item.path.split('?');
  const currentParams = new URLSearchParams(location.search);
  const hasTabParam = (currentParams.get('tab') ?? '') !== '';
  const isDefaultActive =
    isDefaultForGroup === true &&
    location.pathname === itemPathname &&
    !hasTabParam;
  const isActive = exactActive || isDefaultActive;
  const showUnread = item.name === 'Podium' && hasUnread === true;
  const showPending = item.name === 'Approvals' && hasPending === true;

  const restingColor =
    tone === 'muted'
      ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
      : 'text-foreground hover:bg-muted hover:text-foreground';

  // Active styling differs by row depth.
  //
  // Top-level rows light up with the full gold treatment (`bg-primary
  // text-primary-foreground shadow-raised translate-x-1`) — that's
  // the established active-product highlight in both portals.
  //
  // Nested sub-page rows inside a `<SidebarNavGroup>` use a softer
  // `bg-muted text-foreground` instead so the active product (parent
  // row) keeps its gold highlight while the active sub-page reads as
  // a secondary, "you are here within this product" affordance. See
  // contestant-admin-ui-polish spec, Requirements 5.1, 5.2, 5.3.
  const activeClass = nested
    ? 'bg-muted text-foreground'
    : 'bg-primary text-primary-foreground shadow-raised translate-x-1';

  return (
    <Link
      key={item.path}
      to={item.path}
      onClick={onNavClick}
      className={cn(
        'flex items-center gap-4 px-4 py-3 min-h-[var(--touch-target-min)] transition-all duration-300 group relative rounded-lg',
        renderCollapsed ? 'justify-center' : '',
        nested ? 'py-2' : '',
        isActive ? activeClass : restingColor,
      )}
    >
      <div className="relative">
        <Icon
          className={cn(
            'w-4 h-4 shrink-0',
            isActive
              ? nested
                ? 'text-foreground'
                : 'text-primary-foreground'
              : tone === 'muted'
                ? 'text-muted-foreground group-hover:text-foreground'
                : 'text-muted-foreground group-hover:text-foreground',
          )}
        />
        {showUnread && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
        )}
        {showPending && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </div>
      {!renderCollapsed && (
        <span className="text-nav-item">{item.label ?? item.name}</span>
      )}
      {renderCollapsed && (
        <div className="absolute left-full ml-4 px-3 py-2 bg-foreground text-background text-nav-item rounded-sm opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-prominent border border-border">
          {item.label ?? item.name}
        </div>
      )}
    </Link>
  );
}
